import { useState, useEffect } from 'react';
import { useDataRefresh } from '@/lib/events';
import { Plus, Trash2, Pencil, Building2, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import AddItemModal from '../components/AddItemModal';
import EditItemModal from '../components/EditItemModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Liability {
  id: string;
  name: string;
  type: string;
  amount: number;
  interest_rate: number | null;
  description: string;
}

export default function Liabilities() {
  const { profile } = useAuth();
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLiability, setSelectedLiability] = useState<Liability | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    fetchLiabilities();
  }, []);

  useEffect(useDataRefresh(fetchLiabilities), []);

  async function fetchLiabilities() {
    try {
      setError(null);
      const { data, error } = await supabase.from('liabilities').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setLiabilities(data || []);
      setTotalAmount(data?.reduce((sum, liability) => sum + Number(liability.amount), 0) || 0);
    } catch {
      setError('Failed to load liabilities');
    } finally {
      setIsLoading(false);
    }
  }

  const handleDelete = async () => {
    if (!selectedLiability) return;
    try {
      setError(null);
      const { error } = await supabase.from('liabilities').delete().eq('id', selectedLiability.id);
      if (error) throw error;
      await fetchLiabilities();
      setShowDeleteModal(false);
      setSelectedLiability(null);
    } catch {
      setError('Failed to delete liability');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: profile?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Liabilities</h1>
        <Button onClick={() => setShowAddModal(true)} className="gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift">
          <Plus className="w-5 h-5" />
          Add Liability
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-900">Liabilities</h2>
          <span className="text-sm text-slate-600">Total: {formatCurrency(totalAmount)}</span>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-200">
            {liabilities.map((liability) => (
              <div key={liability.id} className="p-4 hover:bg-slate-50/80 transition-colors group">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      {liability.type === 'mortgage' ? (
                        <Building2 className="w-6 h-6 text-slate-700" />
                      ) : (
                        <CreditCard className="w-6 h-6 text-slate-700" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{liability.name}</h3>
                      <p className="text-sm text-slate-600 capitalize">{liability.type.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-lg font-semibold text-slate-900">{formatCurrency(liability.amount)}</div>
                      {liability.interest_rate != null && (
                        <div className="text-sm text-slate-600">{liability.interest_rate}% interest rate</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100" onClick={() => { setSelectedLiability(liability); setShowEditModal(true); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => { setSelectedLiability(liability); setShowDeleteModal(true); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {liabilities.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-slate-600">No liabilities found</p>
                <Button variant="link" onClick={() => setShowAddModal(true)} className="mt-2">
                  Add your first liability
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {showAddModal && (
        <AddItemModal onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchLiabilities(); }} />
      )}

      {showEditModal && selectedLiability && (
        <EditItemModal
          onClose={() => { setShowEditModal(false); setSelectedLiability(null); }}
          onSuccess={() => { setShowEditModal(false); setSelectedLiability(null); fetchLiabilities(); }}
          item={selectedLiability}
          itemType="liability"
        />
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedLiability(null); }}
        onConfirm={handleDelete}
        itemName={selectedLiability?.name || ''}
        itemType="Liability"
      />
    </div>
  );
}
